import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { getApiUrl, getAuthEndpoints } from '../config/api';
import {
  getAuthStatus,
  setAuthCookies,
  clearAuthCookies,
} from '../utils/cookies';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  user: { username: string } | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<{ username: string } | null>(null);

  useEffect(() => {
    // Check if user is authenticated on app load
    const checkAuth = () => {
      const {
        isAuthenticated: authStatus,
        userName,
        userData,
      } = getAuthStatus();

      if (authStatus && userName) {
        setIsAuthenticated(true);
        setUser(userData || { username: userName });
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (
    username: string,
    password: string
  ): Promise<boolean> => {
    try {
      const authEndpoints = getAuthEndpoints();
      const loginUrl = getApiUrl(authEndpoints.LOGIN);

      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Đăng nhập thất bại');
      }

      const data = await response.json();

      // Store authentication data in cookies
      setAuthCookies({
        token: data.access,
        refreshToken: data.refresh,
        userName: username,
        userData: data.user,
      });

      if (data.user) {
        setUser(data.user);
      } else {
        setUser({ username });
      }

      setIsAuthenticated(true);
      return true;
    } catch (error) {
      return false;
    }
  };

  const logout = async () => {
    try {
      const { token } = getAuthStatus();
      const authEndpoints = getAuthEndpoints();
      const logoutUrl = getApiUrl(authEndpoints.LOGOUT);

      // Try to call logout API if token exists
      if (token) {
        await fetch(logoutUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }).catch(error => {
          console.error('Logout API error:', error);
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear all authentication cookies
      clearAuthCookies();

      // Update state
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    login,
    logout,
    user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
